import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Collapse,
  Flex,
  FlexProps,
  FormErrorMessage,
  FormErrorMessageProps,
  FormControl,
  FormControlProps,
  IconButton,
  IconButtonProps,
  Input,
  InputProps,
  Text,
  TextProps,
} from "@chakra-ui/react";
import { useState } from "react";
import { MdClose } from "react-icons/md";
import { useValidateVoucherMutation } from "store/services/booking";
import { VoucherValidateData } from "types/api";

export interface VoucherFieldProps {
  /**
   * The basket, by half, in rupees. Both are sent even when one is 0 — the server needs to know a
   * half is empty to refuse, say, a food-only code on a ticket-only order with a message that
   * explains itself.
   */
  ticketSubtotal?: number;
  foodSubtotal?: number;
  /** Ticket checkouts only; lets the server evaluate weekday, movie and blackout rules. */
  movieShow?: string;
  /** Ticket checkouts only; required for a SEATS voucher to be priced exactly. */
  seatPrices?: number[];
  applied: VoucherValidateData | null;
  onApplied: (voucher: VoucherValidateData | null) => void;
  isDisabled?: boolean;
  containerProps?: BoxProps;
  toggleButtonProps?: ButtonProps;
  formControlProps?: FormControlProps;
  inputGroupProps?: FlexProps;
  inputProps?: InputProps;
  applyButtonProps?: ButtonProps;
  errorMessageProps?: FormErrorMessageProps;
  appliedContainerProps?: FlexProps;
  appliedCodeTextProps?: TextProps;
  appliedTitleTextProps?: TextProps;
  removeButtonProps?: IconButtonProps;
}

/**
 * The customer's discount-code box.
 *
 * Applying is a PREVIEW, not a claim on the code: the server prices it and returns what it would
 * save, but takes no hold until payment is actually initiated. That is deliberate — somebody
 * trying three codes to see which is best should not lock all three for five minutes.
 *
 * Every rule lives on the server and so does every rejection message. Nothing here decides whether
 * a code is valid, or rewrites why it was not: the messages are written to be read by customers
 * ("this voucher can only be used at the counter", "someone is using it right now"), and replacing
 * them with a generic failure is how a recoverable situation gets read as a dead end.
 */
function VoucherField({
  ticketSubtotal = 0,
  foodSubtotal = 0,
  movieShow,
  seatPrices,
  applied,
  onApplied,
  isDisabled = false,
  containerProps,
  toggleButtonProps,
  formControlProps,
  inputGroupProps,
  inputProps,
  applyButtonProps,
  errorMessageProps,
  appliedContainerProps,
  appliedCodeTextProps,
  appliedTitleTextProps,
  removeButtonProps,
}: VoucherFieldProps) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [validateVoucher, { isLoading }] = useValidateVoucherMutation();

  const apply = () => {
    const trimmed = code.trim();
    if (!trimmed) return;

    setError(null);
    validateVoucher({
      code: trimmed,
      ...(movieShow ? { movie_show: movieShow } : {}),
      ...(seatPrices?.length ? { seat_prices: seatPrices } : {}),
      ticket_subtotal: ticketSubtotal,
      food_subtotal: foodSubtotal,
    })
      .unwrap()
      .then((res) => {
        if (res?.data) onApplied(res.data);
      })
      .catch((err) => {
        onApplied(null);
        setError(
          err?.data?.message ||
            err?.message ||
            "That code could not be applied. Please check it and try again.",
        );
      });
  };

  const remove = () => {
    onApplied(null);
    setCode("");
    setError(null);
  };

  if (applied) {
    return (
      <Flex
        align="center"
        justify="space-between"
        gap="0.5rem"
        mb="1rem"
        px="0.75rem"
        py="0.5rem"
        borderWidth="1px"
        borderRadius="md"
        borderColor="green.400"
        {...appliedContainerProps}
      >
        <Box>
          <Text fontSize="sm" fontWeight={600} {...appliedCodeTextProps}>
            {applied.code} applied
          </Text>
          <Text fontSize="xs" color="surface.300" {...appliedTitleTextProps}>
            {applied.title}
          </Text>
        </Box>
        <IconButton
          aria-label="Remove discount code"
          icon={<MdClose />}
          size="sm"
          variant="ghost"
          onClick={remove}
          isDisabled={isDisabled}
          {...removeButtonProps}
        />
      </Flex>
    );
  }

  return (
    <Box mb="1rem" {...containerProps}>
      {/* Collapsed by default: most customers have no code, and an empty input sitting above
          "Proceed to Pay" competes with the thing they actually came to do. */}
      <Button
        variant="link"
        size="sm"
        onClick={() => setIsOpen((open) => !open)}
        isDisabled={isDisabled}
        {...toggleButtonProps}
      >
        Have a discount code?
      </Button>

      <Collapse in={isOpen} animateOpacity>
        <FormControl isInvalid={!!error} mt="0.5rem" {...formControlProps}>
          <Flex gap="0.5rem" {...inputGroupProps}>
            <Input
              value={code}
              onChange={(event) => {
                setCode(event.target.value.toUpperCase());
                setError(null);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  apply();
                }
              }}
              placeholder="Enter code"
              size="sm"
              isDisabled={isDisabled || isLoading}
              autoComplete="off"
              {...inputProps}
            />
            <Button
              size="sm"
              onClick={apply}
              isLoading={isLoading}
              isDisabled={isDisabled || !code.trim()}
              {...applyButtonProps}
            >
              Apply
            </Button>
          </Flex>
          <FormErrorMessage fontSize="xs" {...errorMessageProps}>
            {error}
          </FormErrorMessage>
        </FormControl>
      </Collapse>
    </Box>
  );
}

export default VoucherField;
